import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Helmet } from "react-helmet";
import { withRouter } from "../../../utils/withRouter";
import api from "../../../const/axios";
import QuestionView, { QuestionViewData } from "../../Elements/QuestionView";

interface QuestionPageProps {
  params: { id: string };
}

const QuestionPage: React.FC<QuestionPageProps> = ({ params }) => {
  const [data, setData] = useState<QuestionViewData | null>(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null);
    setNotFound(false);
    api.get(`questions/${params.id}/view`)
      .then(res => setData(res.data))
      .catch(err => {
        if (err?.response?.status === 404) setNotFound(true);
      });
  }, [params.id]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pt-20 flex items-center justify-center">
        <div className="text-center">
          <p className="text-2xl font-bold text-gray-700 dark:text-gray-200">Sual tapılmadı</p>
          <Link to="/" className="mt-4 inline-block text-blue-600 hover:underline text-sm dark:text-indigo-300">Ana səhifəyə qayıt</Link>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pt-20 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const q = data.question;
  const plainText = q.values[0]?.text?.replace(/\/-\//g, " ") ?? "";
  const pageUrl = `https://3sual.az/questions/${params.id}`;
  const description = `${data.packageName} paketindən sual${data.gameName ? ` (${data.gameName})` : ""}: ${plainText.slice(0, 140)}`;
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CreativeWork",
    "@id": pageUrl,
    name: plainText.slice(0, 110) || "Sual",
    text: plainText,
    inLanguage: "az",
    genre: "reference",
    isAccessibleForFree: true,
    isPartOf: { "@id": "https://3sual.az/#website" },
    author: q.authors.map(a => ({ "@type": "Person", name: a.fullname })),
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pt-16">
      <Helmet>
        <title>
          {plainText.slice(0, 60) || "Sual"} | Sual ensiklopediyası — 3sual.az
        </title>
        <meta name="robots" content="index" />
        <meta name="description" content={description} />
        <link rel="canonical" href={pageUrl} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div className="max-w-2xl mx-auto px-4 py-8 space-y-4">
        {/* Breadcrumb */}
        {data.packageId > 0 && (
          <Link
            to={`/packages/${data.packageId}`}
            className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-medium dark:text-indigo-300 dark:hover:text-indigo-200"
          >
            <ChevronLeft className="w-4 h-4" />
            {data.packageName || "Paket"}
          </Link>
        )}
        {data.phaseName && (
          <p className="text-xs text-gray-400 dark:text-gray-500 -mt-2 ml-0.5 flex items-center gap-1">
            <ChevronRight className="w-3 h-3" />
            {data.phaseName}
          </p>
        )}

        {/* Question card */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm px-6 py-5">
          <QuestionView key={params.id} question={q} />
        </div>
      </div>
    </div>
  );
};

export default withRouter(QuestionPage);
